import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { SelectField } from '../components/fields'
import { useAdminContext } from '../session'
import type { DataRequest, DataRequestKind, DataRequestStatus } from '../types'
import type { Page } from '../useCollection'

const STATUSES: DataRequestStatus[] = ['received', 'verified', 'in_progress', 'done', 'rejected']
const KINDS: DataRequestKind[] = ['access', 'deletion', 'rectification', 'portability']

/** Les rubriques du décompte d'effacement, dans l'ordre où on les lit. */
const ERASURE_COUNTS = [
  'customers',
  'attendees',
  'candidates',
  'jurors',
  'table_requests',
  'single_preferences',
  'votes',
  'notifications',
] as const

type ErasureReport = Record<(typeof ERASURE_COUNTS)[number], number>

/** En deçà, l'échéance s'affiche en alerte : une semaine suffit à peine à traiter. */
const DEADLINE_WARNING_DAYS = 7

/**
 * Les demandes RGPD : accès, rectification, effacement, portabilité.
 *
 * Le délai légal est d'**un mois** (art. 12 RGPD). On l'affiche sur chaque
 * demande encore ouverte, et en couleur quand il approche : une demande
 * oubliée au fond d'une liste est exactement ce qui finit en réclamation
 * auprès de l'autorité de contrôle.
 *
 * Une demande non confirmée (« reçue ») ne s'exécute pas : n'importe qui peut
 * taper n'importe quelle adresse dans le formulaire public. Tant que le
 * titulaire de la boîte n'a pas cliqué sur son lien, rien ne prouve que la
 * demande vient de lui — et effacer les données d'un autre est irréversible.
 */
export function AdminGdprPage() {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()
  const [status, setStatus] = useState<DataRequestStatus | ''>('')
  const [kind, setKind] = useState<DataRequestKind | ''>('')
  const [page, setPage] = useState(1)
  const [openRow, setOpenRow] = useState<string | null>(null)

  useEffect(() => {
    document.title = t('admin.gdpr.title')
  }, [t])

  const query = useAdminQuery<Page<DataRequest>>(
    ['admin-data-requests', status, kind, page],
    `/admin/data-requests?page=${page}&page_size=50` +
      (status ? `&status=${status}` : '') +
      (kind ? `&kind=${kind}` : ''),
    eventId,
  )

  const canExecute = can('admin.gdpr.execute')

  return (
    <>
      <AdminHeader title={t('admin.gdpr.title')} description={t('admin.gdpr.intro')} />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 sm:max-w-xl">
        <SelectField<DataRequestStatus | ''>
          label={t('admin.gdpr.filterStatus')}
          value={status}
          onChange={(value) => {
            setStatus(value)
            // Un filtre resserré sur une page lointaine n'afficherait rien.
            setPage(1)
          }}
          options={[
            { value: '', label: t('admin.gdpr.all') },
            ...STATUSES.map((value) => ({ value, label: t(`admin.gdpr.status.${value}`) })),
          ]}
        />
        <SelectField<DataRequestKind | ''>
          label={t('admin.gdpr.filterKind')}
          value={kind}
          onChange={(value) => {
            setKind(value)
            setPage(1)
          }}
          options={[
            { value: '', label: t('admin.gdpr.all') },
            ...KINDS.map((value) => ({ value, label: t(`admin.gdpr.kind.${value}`) })),
          ]}
        />
      </div>

      {query.isPending ? <Skeleton className="h-96 w-full" /> : null}
      {query.isError ? (
        <p className="text-sm text-state-danger">{query.error.message}</p>
      ) : null}

      {query.data && query.data.items.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('admin.gdpr.empty')}</p>
      ) : null}

      <div className="grid gap-1.5">
        {(query.data?.items ?? []).map((row) => (
          <RequestRow
            key={row.id}
            row={row}
            eventId={eventId}
            canExecute={canExecute}
            open={openRow === row.id}
            onToggle={() => setOpenRow(openRow === row.id ? null : row.id)}
          />
        ))}
      </div>

      {query.data && query.data.pages > 1 ? (
        <div className="mt-6 flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            {t('admin.audit.previous')}
          </Button>
          <span className="text-caption tabular-nums text-content-muted">
            {page} / {query.data.pages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= query.data.pages}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('admin.audit.next')}
          </Button>
        </div>
      ) : null}
    </>
  )
}

/** Une demande est « ouverte » tant qu'elle n'a reçu ni exécution ni refus. */
function isOpen(status: DataRequestStatus): boolean {
  return status !== 'done' && status !== 'rejected'
}

/**
 * L'échéance légale : réception + un mois calendaire.
 *
 * Un mois, pas trente jours : c'est la lettre de l'article 12. `setMonth`
 * déborde sur le mois suivant un 31 janvier (→ 3 mars), ce qui donne
 * quelques jours de trop ; on recale alors sur le dernier jour du mois visé
 * pour ne jamais afficher une échéance plus tardive que la vraie.
 */
function legalDeadline(createdAt: string): Date {
  const start = new Date(createdAt)
  const deadline = new Date(start)
  deadline.setMonth(start.getMonth() + 1)
  if (deadline.getDate() !== start.getDate()) deadline.setDate(0)
  return deadline
}

function statusTone(status: DataRequestStatus): 'neutral' | 'ember' | 'champagne' | 'success' {
  if (status === 'done') return 'success'
  if (status === 'verified') return 'champagne'
  if (status === 'in_progress') return 'ember'
  return 'neutral'
}

function RequestRow({
  row,
  eventId,
  canExecute,
  open,
  onToggle,
}: {
  row: DataRequest
  eventId: string | undefined
  canExecute: boolean
  open: boolean
  onToggle: () => void
}) {
  const { t, i18n } = useTranslation()
  // Tenu ici et non dans les actions : celles-ci disparaissent dès que la
  // demande passe « traitée », et le message partirait avec elles.
  const [done, setDone] = useState<string | null>(null)

  const deadline = legalDeadline(row.created_at)
  const daysLeft = Math.ceil((deadline.getTime() - Date.now()) / 86_400_000)
  const pending = isOpen(row.status)
  // L'échéance n'a de sens que tant que la demande attend : une demande close
  // dans les temps n'a pas à rester en rouge.
  const deadlineState = !pending
    ? 'closed'
    : daysLeft < 0
      ? 'overdue'
      : daysLeft <= DEADLINE_WARNING_DAYS
        ? 'soon'
        : 'ok'

  const shortDate = (iso: string | Date) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="min-w-0 flex-1 basis-48 break-all text-sm text-content-primary">
            {row.email}
          </span>
          <Badge tone="neutral">{t(`admin.gdpr.kind.${row.kind}`)}</Badge>
          <Badge tone={statusTone(row.status)}>{t(`admin.gdpr.status.${row.status}`)}</Badge>
          <span aria-hidden className="shrink-0 text-content-muted">
            {open ? '−' : '+'}
          </span>
        </span>
        <span className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-caption tabular-nums text-content-muted">
          <span>{t('admin.gdpr.receivedOn', { date: shortDate(row.created_at) })}</span>
          {pending ? (
            <span
              className={cn(
                deadlineState === 'overdue' && 'font-medium text-state-danger',
                deadlineState === 'soon' && 'font-medium text-state-warning',
              )}
            >
              {deadlineState === 'overdue'
                ? t('admin.gdpr.deadlinePast', { date: shortDate(deadline) })
                : t('admin.gdpr.deadline', { date: shortDate(deadline), count: daysLeft })}
            </span>
          ) : row.handled_at ? (
            <span>{t('admin.gdpr.handledOn', { date: shortDate(row.handled_at) })}</span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div className="space-y-4 border-t border-line-subtle px-4 py-4">
          <RequestDetails row={row} shortDate={shortDate} />
          {row.status === 'received' ? (
            // On explique plutôt que de griser des boutons sans raison :
            // « pourquoi je ne peux pas l'exécuter ? » est la première
            // question qu'on se pose devant une demande reçue.
            <p className="rounded-md border border-line-subtle bg-bg-overlay px-3 py-2 text-sm text-content-secondary">
              {t('admin.gdpr.awaitingVerification')}
            </p>
          ) : null}
          {done ? (
            // Le message reste affiché après le geste : la liste se
            // rafraîchit et la ligne change de statut, mais c'est ici qu'on
            // regarde juste après avoir cliqué.
            <p className="rounded-md border border-state-success/40 px-3 py-2 text-sm text-state-success">
              {done}
            </p>
          ) : null}
          {pending && row.status !== 'received' ? (
            canExecute ? (
              <RequestActions row={row} eventId={eventId} onDone={setDone} />
            ) : (
              <p className="text-sm text-content-muted">{t('admin.gdpr.noPermission')}</p>
            )
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}

function RequestDetails({
  row,
  shortDate,
}: {
  row: DataRequest
  shortDate: (iso: string) => string
}) {
  const { t } = useTranslation()
  const counts = erasureCounts(row.result)

  return (
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      <div className="sm:col-span-2">
        <dt className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.gdpr.details')}
        </dt>
        <dd className="mt-1 whitespace-pre-line break-words text-content-primary">
          {row.details?.trim() ? row.details : t('admin.gdpr.noDetails')}
        </dd>
      </div>
      <div>
        <dt className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.gdpr.locale')}
        </dt>
        {/* La langue dans laquelle répondre au demandeur. */}
        <dd className="mt-1 uppercase text-content-primary">{row.locale ?? '—'}</dd>
      </div>
      {row.handled_at ? (
        <div>
          <dt className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.gdpr.handledAt')}
          </dt>
          <dd className="mt-1 text-content-primary">{shortDate(row.handled_at)}</dd>
        </div>
      ) : null}
      {row.notes ? (
        <div className="sm:col-span-2">
          <dt className="text-caption uppercase tracking-wide text-content-muted">
            {row.status === 'rejected' ? t('admin.gdpr.rejectReason') : t('admin.gdpr.notes')}
          </dt>
          <dd className="mt-1 whitespace-pre-line break-words text-content-primary">
            {row.notes}
          </dd>
        </div>
      ) : null}
      {counts ? (
        <div className="sm:col-span-2">
          <dt className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.gdpr.erasureResult')}
          </dt>
          <dd className="mt-1">
            <ErasureCounts report={counts} />
          </dd>
        </div>
      ) : row.result !== null && row.result !== undefined && row.status === 'done' ? (
        <div className="sm:col-span-2">
          <dt className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.gdpr.result')}
          </dt>
          <dd className="mt-1 text-content-primary">{t('admin.gdpr.resultHandled')}</dd>
        </div>
      ) : null}
    </dl>
  )
}

/**
 * Le décompte d'un effacement, s'il en est un.
 *
 * `result` est libre côté serveur (« dossier remis » pour un accès) : on ne
 * l'affiche en tableau que s'il a bien la forme d'un décompte.
 */
function erasureCounts(result: unknown): ErasureReport | null {
  if (!result || typeof result !== 'object') return null
  const record = result as Record<string, unknown>
  if (!ERASURE_COUNTS.every((key) => typeof record[key] === 'number')) return null
  return record as ErasureReport
}

function ErasureCounts({ report }: { report: ErasureReport }) {
  const { t } = useTranslation()
  return (
    <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
      {ERASURE_COUNTS.map((key) => (
        <li key={key} className="flex justify-between gap-2">
          <span className="text-content-secondary">{t(`admin.gdpr.counts.${key}`)}</span>
          <span className="tabular-nums text-content-primary">{report[key]}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Exécuter ou rejeter.
 *
 * Ce que fait « Exécuter » dépend du type, et on le dit **avant** le clic :
 * pour un accès, le geste ne fait que consigner (le demandeur a déjà son
 * dossier) ; pour un effacement, il détruit l'identité sans retour possible.
 * Les deux boutons se ressemblent, leurs conséquences non.
 *
 * L'effacement exige de retaper l'adresse : un simple « Confirmer » se clique
 * par réflexe, recopier un e-mail oblige à regarder de qui il s'agit.
 */
function RequestActions({
  row,
  eventId,
  onDone,
}: {
  row: DataRequest
  eventId: string | undefined
  onDone: (message: string) => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'idle' | 'execute' | 'reject'>('idle')
  const [typedEmail, setTypedEmail] = useState('')
  const [reason, setReason] = useState('')

  const invalidate = [['admin-data-requests']]
  const execute = useAdminMutation<ErasureReport | DataRequest, { id: string }>(
    'post',
    ({ id }) => `/admin/data-requests/${id}/execute`,
    eventId,
    invalidate,
  )
  const reject = useAdminMutation<DataRequest, { id: string; reason: string }>(
    'post',
    ({ id }) => `/admin/data-requests/${id}/reject`,
    eventId,
    invalidate,
  )

  const isDeletion = row.kind === 'deletion'
  const emailMatches = typedEmail.trim().toLowerCase() === row.email.trim().toLowerCase()
  const reasonOk = reason.trim().length >= 5

  const errorOf = (error: unknown) =>
    error instanceof ApiError ? error.message : t('admin.gdpr.genericError')

  if (mode === 'execute') {
    return (
      <div className="space-y-3 rounded-md border border-line px-3 py-3">
        <p className="text-sm text-content-primary">{t(`admin.gdpr.executeExplain.${row.kind}`)}</p>
        {isDeletion ? (
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-content-muted">
              {t('admin.gdpr.typeEmail', { email: row.email })}
            </span>
            <input
              type="email"
              value={typedEmail}
              autoComplete="off"
              onChange={(event) => setTypedEmail(event.target.value)}
              className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
            />
          </label>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            // Le libellé de la rectification est long : sur un téléphone, il
            // doit pouvoir passer à la ligne plutôt que déborder de la carte.
            className="h-auto min-h-10 min-w-0 whitespace-normal py-2 text-left"
            disabled={execute.isPending || (isDeletion && !emailMatches)}
            onClick={() =>
              execute.mutate(
                { id: row.id },
                {
                  // Le décompte lui-même s'affiche dans le détail, relu
                  // depuis le serveur : c'est ce qui a réellement été
                  // anonymisé, pas ce qu'on croit avoir demandé.
                  onSuccess: () =>
                    onDone(isDeletion ? t('admin.gdpr.erasureDone') : t('admin.gdpr.executeDone')),
                },
              )
            }
          >
            {isDeletion ? t('admin.gdpr.confirmErase') : t(`admin.gdpr.confirmExecute.${row.kind}`)}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
            {t('admin.cancel')}
          </Button>
        </div>
        {execute.isError ? (
          <p className="text-sm text-state-danger">{errorOf(execute.error)}</p>
        ) : null}
      </div>
    )
  }

  if (mode === 'reject') {
    return (
      <div className="space-y-3 rounded-md border border-line px-3 py-3">
        <label className="block">
          <span className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.gdpr.rejectReason')}
          </span>
          <textarea
            value={reason}
            rows={3}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('admin.gdpr.rejectHint')}
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={reject.isPending || !reasonOk}
            onClick={() =>
              reject.mutate(
                { id: row.id, reason: reason.trim() },
                { onSuccess: () => onDone(t('admin.gdpr.rejectDone')) },
              )
            }
          >
            {t('admin.gdpr.confirmReject')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
            {t('admin.cancel')}
          </Button>
        </div>
        {reject.isError ? (
          <p className="text-sm text-state-danger">{errorOf(reject.error)}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" onClick={() => setMode('execute')}>
        {t('admin.gdpr.execute')}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setMode('reject')}>
        {t('admin.gdpr.reject')}
      </Button>
    </div>
  )
}
